import { useEffect, useRef, useState } from "react";
import type { ActPrompt } from "../types";

export interface Bubble {
  role: "user" | "assistant";
  content: string;
  incomplete?: boolean;
}

export function shouldSendComposerKey(e: { key: string; shiftKey: boolean }) {
  return e.key === "Enter" && !e.shiftKey;
}

export function chatMessagesForTurn(text: string): Bubble[] {
  return [
    { role: "user", content: text },
    { role: "assistant", content: "", incomplete: true },
  ];
}

export function Chat({ transcriptId }: { transcriptId: string }) {
  const [messages, setMessages] = useState<Bubble[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<number | null>(null);
  const [actions, setActions] = useState<ActPrompt[]>([]);
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const sendingRef = useRef(false);

  // Load quick actions only; previous chat turns stay persisted but hidden here.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/act-prompts")
      .then((r) => (r.ok ? r.json() : []))
      .then((a: ActPrompt[]) => {
        if (!cancelled) setActions(a);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [transcriptId]);

  // Abort any in-flight stream when the component unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    logRef.current?.scrollTo(0, logRef.current.scrollHeight);
  }, [messages]);

  // ⌘K / Ctrl-K focuses the ask input (the design's command affordance).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        // Don't steal focus while the user is typing in another field.
        const t = e.target as HTMLElement;
        if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function copy(i: number, text: string) {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(i);
      setTimeout(() => setCopied((c) => (c === i ? null : c)), 1200);
    });
  }

  // Replace the trailing assistant bubble as tokens arrive.
  function setAssistant(content: string, incomplete: boolean) {
    setMessages((prev) => {
      const next = [...prev];
      next[next.length - 1] = { role: "assistant", content, incomplete };
      return next;
    });
  }

  async function send(preset?: string) {
    const text = (preset ?? input).trim();
    if (!text || sendingRef.current) return;
    sendingRef.current = true; // synchronous in-flight guard (beats stale `streaming`)
    if (!preset) setInput(""); // only typed sends clear the box; presets keep the draft
    setError(null);
    setCopied(null);
    setMessages(chatMessagesForTurn(text));
    setStreaming(true);

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    let acc = "";
    let done = false;
    try {
      const res = await fetch(`/api/transcripts/${transcriptId}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
        signal: ac.signal,
      });
      if (!res.ok || !res.body) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Chat failed (${res.status})`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { value, done: rdone } = await reader.read();
        if (rdone) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() ?? "";
        for (const frame of frames) {
          const line = frame.replace(/^data: /, "").trim();
          if (!line) continue;
          const obj = JSON.parse(line) as { token?: string; done?: boolean; error?: string };
          if (obj.error) throw new Error(obj.error);
          if (obj.token) {
            acc += obj.token;
            setAssistant(acc, true);
          } else if (obj.done) {
            done = true;
            setAssistant(acc, false);
          }
        }
      }
      if (!done) setAssistant(acc, true); // stream ended without a done frame
    } catch (e) {
      if ((e as Error)?.name === "AbortError") return; // superseded/unmounted — silent
      setError(e instanceof Error ? e.message : "Chat failed");
      setAssistant(acc, true); // keep whatever streamed, marked incomplete
    } finally {
      setStreaming(false);
      sendingRef.current = false;
    }
  }

  return (
    <div className="chat">
      {/* role=log: message additions are announced politely by default */}
      <div className="chat-log" role="log" ref={logRef}>
        {messages.map((m, i) => (
          <div key={i} className={`msg ${m.role}`}>
            <div className={`bubble ${m.role}${m.incomplete ? " incomplete" : ""}`}>
              {m.content || (m.incomplete ? "…" : "")}
            </div>
            {m.content && (
              <button
                className="copy-btn"
                aria-label="Copy message"
                onClick={() => copy(i, m.content)}
              >
                <span aria-hidden>{copied === i ? "✓ copied" : "📋 copy"}</span>
                {/* announces the flip to "copied"; empty on reset, so no echo */}
                <span className="sr-only" role="status">
                  {copied === i ? "Copied" : ""}
                </span>
              </button>
            )}
          </div>
        ))}
      </div>
      {error && (
        <p className="chat-error" role="alert">
          {error}
        </p>
      )}

      <div className="composer">
        <div className="cmdbar">
          {actions.map((a) => (
            <button
              key={a.key}
              className="action"
              disabled={streaming}
              onClick={() => {
                if (a.mode === "send") void send(a.prompt);
                else {
                  setInput(a.prompt);
                  inputRef.current?.focus();
                }
              }}
            >
              <span aria-hidden>{a.glyph}</span> {a.label}
            </button>
          ))}
        </div>
        <form
          className="chat-form"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <textarea
            ref={inputRef}
            name="ask"
            aria-label="Ask about this transcript"
            value={input}
            disabled={streaming}
            rows={3}
            placeholder="Ask anything about this transcript…"
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (!shouldSendComposerKey(e)) return;
              e.preventDefault();
              void send();
            }}
          />
          <button type="submit" disabled={streaming || !input.trim()} aria-label="Send">
            ↑
          </button>
        </form>
      </div>
    </div>
  );
}
